import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const GAP = 7;
const HUGE_H = 1e7;
const EXPAND_MAX = 320;
const EXPAND_THRESHOLD = 40;

/**
 * MAXRECTS bin-packing (Jukka Jylänki, 2010) with Best-Short-Side-Fit choice.
 * Processes items in caller-supplied order so drag-and-drop reordering shows up
 * visually. Tracks free space as a list of maximal rectangles; for each tile
 * picks the rectangle that minimizes the leftover on its shorter side (BSSF is
 * Jylänki's recommended heuristic for density). After placement the chosen
 * rect and any overlapping rects are split into up to 4 sub-rects; rects fully
 * contained in another are pruned.
 *
 * `expandableIds` is an optional Set of item IDs that may grow into the empty
 * horizontal gap to their right (up to a 320px cap). Used for shrink-to-fit
 * tiles whose visual rhythm suffers when neighbours leave them stranded.
 */
export function useTetrisMasonry(itemIds, expandableIds) {
  const containerRef = useRef(null);
  const tileRefs = useRef({});
  // Natural (pre-expansion) tile sizes. Cached on first measurement so that
  // when we later apply an `expandedWidth` and the ResizeObserver fires, the
  // recompute still treats the tile as its original width — otherwise we'd
  // pack the layout using the expanded width and the algorithm would chase its
  // tail, possibly looping.
  const naturalSizesRef = useRef({});
  const [state, setState] = useState({ positions: {}, widths: {}, height: 0, ready: false });

  const compute = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const cw = container.clientWidth;
    if (cw === 0) return;

    let freeRects = [{ x: 0, y: 0, w: cw, h: HUGE_H }];
    const positions = {};
    const sizes = {};
    let maxH = 0;

    for (const id of itemIds) {
      const el = tileRefs.current[id];
      if (!el) continue;
      let size = naturalSizesRef.current[id];
      if (!size) {
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        if (!w || !h) continue;
        size = { w, h };
        naturalSizesRef.current[id] = size;
      }
      const { w, h } = size;
      sizes[id] = size;

      const fw = Math.min(cw, w + GAP);
      const fh = h + GAP;

      // Best-Short-Side-Fit: pick free rect that minimizes the smaller of
      // (rect.w - tile.w, rect.h - tile.h). Tiebreak: lower y, then lower x.
      let best = null;
      let bestScore = Infinity;
      for (const r of freeRects) {
        if (r.w < fw || r.h < fh) continue;
        const shortSide = Math.min(r.w - fw, r.h - fh);
        if (
          shortSide < bestScore ||
          (shortSide === bestScore && best && (r.y < best.y || (r.y === best.y && r.x < best.x)))
        ) {
          bestScore = shortSide;
          best = r;
        }
      }

      let px, py;
      if (best) {
        px = best.x;
        py = best.y;
      } else {
        px = 0;
        py = maxH + GAP;
      }

      positions[id] = { x: px, y: py };
      if (py + h > maxH) maxH = py + h;

      const itemRight = px + fw;
      const itemBottom = py + fh;

      const next = [];
      for (const r of freeRects) {
        const rRight = r.x + r.w;
        const rBottom = r.y + r.h;
        const overlap =
          px < rRight && itemRight > r.x && py < rBottom && itemBottom > r.y;
        if (!overlap) {
          next.push(r);
          continue;
        }
        if (px > r.x) next.push({ x: r.x, y: r.y, w: px - r.x, h: r.h });
        if (itemRight < rRight) next.push({ x: itemRight, y: r.y, w: rRight - itemRight, h: r.h });
        if (py > r.y) next.push({ x: r.x, y: r.y, w: r.w, h: py - r.y });
        if (itemBottom < rBottom) next.push({ x: r.x, y: itemBottom, w: r.w, h: rBottom - itemBottom });
      }

      // Prune rects fully contained in another rect.
      freeRects = next.filter((a, i) => {
        for (let j = 0; j < next.length; j++) {
          if (j === i) continue;
          const b = next[j];
          if (
            b.x <= a.x &&
            b.y <= a.y &&
            b.x + b.w >= a.x + a.w &&
            b.y + b.h >= a.y + a.h &&
            (b.x < a.x || b.y < a.y || b.x + b.w > a.x + a.w || b.y + b.h > a.y + a.h ||
              j < i)
          ) {
            return false;
          }
        }
        return true;
      });
    }

    // Post-pack expansion: shrink-to-fit tiles often leave a visible empty
    // strip to their right because the next tile didn't fit. For each
    // expandable tile, scan for the nearest tile whose vertical span overlaps
    // and grow up to that x (or the container right edge), capped at
    // EXPAND_MAX so a 3-word note doesn't become a 600px banner.
    const widths = {};
    if (expandableIds && expandableIds.size > 0) {
      for (const id of itemIds) {
        if (!expandableIds.has(id)) continue;
        const pos = positions[id];
        const size = sizes[id];
        if (!pos || !size) continue;

        const top = pos.y;
        const bottom = pos.y + size.h;
        const right = pos.x + size.w;

        let nearestX = cw;
        for (const otherId of itemIds) {
          if (otherId === id) continue;
          const op = positions[otherId];
          const os = sizes[otherId];
          if (!op || !os) continue;
          if (op.x < right) continue;
          const oTop = op.y;
          const oBottom = op.y + os.h;
          if (oBottom <= top || oTop >= bottom) continue;
          if (op.x < nearestX) nearestX = op.x;
        }

        const available = nearestX - pos.x - GAP;
        const target = Math.min(available, EXPAND_MAX);
        if (target > size.w + EXPAND_THRESHOLD) {
          widths[id] = target;
        }
      }
    }

    setState((prev) => {
      // Skip the state update (and the resulting re-render) if nothing changed
      // structurally. Extra defence against any remaining feedback loop where a
      // recompute produces identical output but a different object reference.
      if (
        prev.ready &&
        prev.height === maxH &&
        sameLayoutMap(prev.positions, positions) &&
        sameWidthMap(prev.widths, widths)
      ) {
        return prev;
      }
      return { positions, widths, height: maxH, ready: true };
    });
  }, [itemIds, expandableIds]);

  useLayoutEffect(() => {
    compute();
  }, [compute]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId = null;
    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        compute();
      });
    };

    // Observe only the container, NOT individual tiles. Once we apply an
    // `expandedWidth` inline style, the tile's offsetWidth changes and a
    // per-tile RO would fire, feeding back into the compute() → widths →
    // width-apply → RO chain. Container-only observation catches window resize
    // (the only external layout trigger we care about) without any feedback.
    const ro = new ResizeObserver(schedule);
    ro.observe(container);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [compute]);

  const registerTile = useCallback((id, el) => {
    if (el) {
      tileRefs.current[id] = el;
    } else {
      delete tileRefs.current[id];
      delete naturalSizesRef.current[id];
    }
  }, []);

  return { containerRef, registerTile, ...state };
}

function sameLayoutMap(a, b) {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    const pa = a[k];
    const pb = b[k];
    if (!pb || pa.x !== pb.x || pa.y !== pb.y) return false;
  }
  return true;
}

function sameWidthMap(a, b) {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}
